import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { LocationViewModel } from "../../../Types/ExportTypes";
import { CountryAutoComplete, AccountAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import DefaultActions from "../../../components/Card/DefaultActions";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const LocationEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { code = "" } = params || {};
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(LocationViewModel) | null>(null);
  const [showAccounts, setShowAccounts] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      authService
        .get<LocationViewModel>(`/settingsApi/GetLocation?code=${code}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      // Default empty location object for creating new entries
      const defaultLocation: LocationViewModel & { originalValues: Partial<LocationViewModel>; hasChanges: boolean } = {
        unit: "",
        code: "",
        description: "",
        returns: false,
        address: "",
        address2: "",
        postCode: "",
        city: "",
        county: "",
        countryCode: "",
        adrMisc: "",
        email: "",
        b2c: false,
        b2b: false,
        default: false,
        blocked: false,
        externalReference1: "",
        inventoryAccount: "",
        inventoryAdjustment: "",
        cogsAccount: "",
        purchaseRecievalAccount: "",
        purchaseOverheadAccount: "",
        transferBalanceAccount: "",
        inventoryValueAdjustmentAccountNo: "",
        createNewLocation: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultLocation.originalValues = { ...defaultLocation };
      setViewModel(defaultLocation);
    }
  }, [code, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateLocation()) return; // Prevent save if validation fails
    var newItem = viewModel.createNewLocation ?? false;
    setIsLoading(true);
    try {
      const response = await authService.post<LocationViewModel>("/settingsApi/CreateOrUpdateLocation", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");
      if (newItem) {
        navigate({ path: "/settings/location/edit", params: { code: response.code } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof LocationViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as LocationViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleCopy = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    var newItem = viewModel?.createNewLocation ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryLocation"), "warning");
      return;
    }
    setIsLoading(true);
    try {
      var response = await authService.post<LocationViewModel>(`/settingsApi/CopyLocation`, viewModel);
      setViewModel(response);
      showSnackbar(t("dialog.copySuccess"), "success");

      navigate({ path: "/settings/location/edit", params: { code: response.code } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyError")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNewLocation ?? false;
    if (newItem) {
      navigate({ path: "/settings/location/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteLocation`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");


      navigate({ path: "/settings/location/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const validateLocation = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.code || viewModel.code.trim() === "") {
      showSnackbar(t("help.codeRequired"), "warning");
      return false;
    }

    if (!viewModel.description || viewModel.description.trim() === "") {
      showSnackbar(t("help.descriptionRequired"), "warning");
      return false;
    }

    return true;
  };


  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.location")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onCopy={handleCopy}
            onDelete={handleDelete}
          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.code")}
                  value={viewModel.code || ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={viewModel.createNewLocation != true}
                  error={!viewModel.code || viewModel.code.trim() === ""}
                  helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t("common.description")}
                  value={viewModel.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.description || viewModel.description.trim() === ""}
                  helperText={!viewModel.description || viewModel.description.trim() === "" ? t("help.descriptionRequired") : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.address")}
                  value={viewModel.address || ""}
                  onChange={(e) => handleChange("address", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.address") + " 2"}
                  value={viewModel.address2 || ""}
                  onChange={(e) => handleChange("address2", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("common.postCode")}
                  value={viewModel.postCode || ""}
                  onChange={(e) => handleChange("postCode", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("common.city")}
                  value={viewModel.city || ""}
                  onChange={(e) => handleChange("city", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <CountryAutoComplete
                    key={viewModel.countryCode || null}
                    valueId={viewModel.countryCode || null}
                    onChange={(selected) => handleChange("countryCode", selected?.value || "")}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t("common.email")}
                  value={viewModel.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.b2c} onChange={(e) => handleChange("b2c", e.target.checked)} />}
                  label={t("common.consumer")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.b2b} onChange={(e) => handleChange("b2b", e.target.checked)} />}
                  label={t("common.business")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.default} onChange={(e) => handleChange("default", e.target.checked)} />}
                  label={t("common.default")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                  label={t("common.blocked")}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showAccounts} onChange={(e) => setShowAccounts(e.target.checked)} />}
                label={t("common.showAccounts")}
              />
            </Grid>

            {/* Settings Section */}
            {showAccounts && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >





                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" >
                      <AccountAutoComplete
                        key={viewModel.inventoryAccount}
                        label={t("common.inventoryAccount")}
                        valueId={viewModel.inventoryAccount}
                        onChange={(selected) => handleChange("inventoryAccount", selected?.value || "")}
                      />
                    </FormControl>
                  </Grid>


                  <Grid item xs={12} >
                    <FormControl fullWidth margin="normal" >
                      <AccountAutoComplete
                        key={viewModel.transferBalanceAccount}
                        label={t("common.transferBalanceAccount")}
                        valueId={viewModel.transferBalanceAccount}
                        onChange={(selected) => handleChange("transferBalanceAccount", selected?.value || "")}
                      />
                    </FormControl>
                  </Grid>


                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>


    </>
  );
};

export default LocationEditPage;
